import { initializeApp, getApps, getApp } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL, connectStorageEmulator } from "firebase/storage";
import {
    getFirestore,
    doc,
    setDoc,
    collection,
    query,
    where,
    getDocs,
    limit,
    updateDoc,
    getDoc as getFirestoreDoc,
    connectFirestoreEmulator
} from "firebase/firestore";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { getFirebaseRuntimeConfig } from "../config/firebaseEnvironment";
import { resolveFirebaseEmulatorConfig } from "../config/firebaseEmulators";

const productionFirebaseConfig = {
    apiKey: "AIzaSyDwgokJ342kxknQlFeOmlLwSI14WsdiDnI",
    authDomain: "portal-sisfol.firebaseapp.com",
    projectId: "portal-sisfol",
    storageBucket: "portal-sisfol.firebasestorage.app",
    messagingSenderId: "693463369463",
    appId: "1:693463369463:web:cc5025a2018494336af5f2",
    measurementId: "G-Z42ZWVLREM",
    databaseURL: "https://portal-sisfol-default-rtdb.firebaseio.com"
};

const {
    firebaseConfig,
    environment: firebaseEnvironment,
    isE2E: isE2EEnvironment,
} = getFirebaseRuntimeConfig(productionFirebaseConfig);
const firebaseEmulatorConfig = resolveFirebaseEmulatorConfig({ environment: firebaseEnvironment });

const firestorePathId = 'sisfol-rh-online';

export const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();

export const storage = getStorage(app);
export const db = getFirestore(app);
export const auth = getAuth(app);
export const functions = getFunctions(app, "us-central1");
export const functionsSouthAmerica = getFunctions(app, "southamerica-east1");
export const functionsUsEast = getFunctions(app, "us-east1");
export const rtdb = getDatabase(app);

if (firebaseEmulatorConfig.enabled) {
    const { auth: authEndpoint, firestore, storage: storageEndpoint, database, functions: functionsEndpoint } = firebaseEmulatorConfig.endpoints;
    connectAuthEmulator(auth, `http://${authEndpoint.host}:${authEndpoint.port}`, { disableWarnings: true });
    connectFirestoreEmulator(db, firestore.host, firestore.port);
    connectStorageEmulator(storage, storageEndpoint.host, storageEndpoint.port);
    connectDatabaseEmulator(rtdb, database.host, database.port);
    [functions, functionsSouthAmerica, functionsUsEast].forEach((functionsInstance) => {
        connectFunctionsEmulator(functionsInstance, functionsEndpoint.host, functionsEndpoint.port);
    });
}
export { firestorePathId, firebaseConfig, firebaseEnvironment, isE2EEnvironment };

/**
 * Funcao para alterar a senha do servidor no Firestore.
 * Esta funcao sera substituida por Cloud Function em fase posterior.
 */
export const updateServerPassword = async (municipioId, cpf, newPassword) => {
    try {
        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const credentialPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/Servidoresacesso/${cleanedCpf}`;
        const credentialDocRef = doc(db, credentialPath);

        await updateDoc(credentialDocRef, {
            senha: newPassword,
            primeiroAcesso: false,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error("Erro ao atualizar a senha do servidor:", error);
        throw error;
    }
};

/**
 * Funcao para fazer upload de um arquivo para o Firebase Storage.
 */
export const uploadFile = async (file, path) => {
    try {
        if (!file || !path) {
            throw new Error("Arquivo ou caminho de upload não fornecidos.");
        }

        const storageRef = ref(storage, path);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        return downloadURL;
    } catch (error) {
        console.error("Erro detalhado ao fazer upload do arquivo:", error);
        throw error;
    }
};

/**
 * Salva ou atualiza os dados de recadastramento em um caminho estruturado com o nome da campanha.
 */
export const saveRecadastramento = async (municipioId, campaignName, data) => {
    try {
        if (!municipioId || !campaignName || !data.userCpf || !data.vinculoId) {
            throw new Error("Dados insuficientes para salvar: municipioId, campaignName, userCpf e vinculoId são obrigatórios.");
        }

        const docId = `${data.userCpf}-${data.vinculoId}`;
        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${docId}`;
        const docRef = doc(db, docPath);

        await setDoc(docRef, data, { merge: true });

        return docId;
    } catch (error) {
        console.error("Erro ao salvar dados no Firestore:", error);
        throw error;
    }
};

/**
 * Busca os dados de recadastramento de um servidor pelo ID do documento.
 */
export const getRecadastramentoById = async (municipioId, campaignName, docId) => {
    try {
        if (!docId || !municipioId || !campaignName) {
            console.warn("getRecadastramentoById: docId, municipioId ou campaignName não fornecidos.");
            return null;
        }

        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${docId}`;
        const docRef = doc(db, docPath);
        const docSnap = await getFirestoreDoc(docRef, { source: 'server' });

        if (docSnap.exists()) {
            return docSnap.data();
        }

        return null;
    } catch (error) {
        console.error("Erro ao buscar recadastramento por ID:", error);
        throw error;
    }
};

/**
 * Busca os dados de recadastramento de um servidor pelo seu userId.
 */
export const getRecadastramentoByUserId = async (municipioId, campaignName, userId) => {
    try {
        if (!userId || !municipioId || !campaignName) {
            return null;
        }

        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos`;
        const collectionRef = collection(db, collectionPath);
        const q = query(collectionRef, where("userId", "==", userId), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            return { id: docSnap.id, ...docSnap.data() };
        }

        return null;
    } catch (error) {
        console.error("Erro ao buscar recadastramento por userId:", error);
        throw error;
    }
};

/**
 * Busca os dados de recadastramento de um servidor pelo seu CPF.
 */
export const getRecadastramentoByCPF = async (municipioId, campaignName, cpf) => {
    try {
        if (!cpf || !municipioId || !campaignName) {
            console.warn("getRecadastramentoByCPF: cpf, municipioId ou campaignName inválido/ausente.");
            return null;
        }

        const collectionPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos`;
        const collectionRef = collection(db, collectionPath);
        const q = query(collectionRef, where("dadosPessoais.cpf.value", "==", cpf), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            return { id: docSnap.id, ...docSnap.data() };
        }

        console.log("Nenhum recadastramento encontrado para o CPF:", cpf, "na campanha:", campaignName);
        return null;
    } catch (error) {
        console.error("Erro ao buscar recadastramento por CPF:", error);
        throw error;
    }
};

/**
 * Busca as regras de campos obrigatorios da campanha ativa.
 */
export const getActiveCampaignRequiredFields = async (municipioId) => {
    try {
        const campaignsCollectionRef = collection(db, `artifacts/${firestorePathId}/public/data/recadastramentoCampaigns`);
        const q = query(
            campaignsCollectionRef,
            where('status', '==', 'ativa'),
            where('municipioId', '==', municipioId),
            limit(1)
        );

        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return { id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() };
        }

        console.warn("AVISO: Nenhuma campanha ativa encontrada.");
        return null;
    } catch (error) {
        console.error("ERRO CRÍTICO ao buscar campanha ativa:", error);
        throw error;
    }
};

/**
 * Atualiza o status de um recadastramento pelo ID do documento.
 */
export const updateRecadastramentoStatus = async (municipioId, campaignName, docId, newStatus) => {
    try {
        if (!docId || !municipioId || !newStatus || !campaignName) {
            throw new Error("docId, municipioId, campaignName e newStatus devem ser fornecidos.");
        }

        const docPath = `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/recadastramentos/${docId}`;
        const docRef = doc(db, docPath);

        await updateDoc(docRef, {
            status: newStatus,
            lastUpdate: new Date(),
        });
    } catch (error) {
        console.error("Erro ao atualizar o status do recadastramento:", error);
        throw error;
    }
};

/**
 * Busca os dados de um municipio especifico pelo seu ID.
 */
export const getMunicipioById = async (municipioId) => {
    try {
        if (!municipioId) {
            console.warn("getMunicipioById: municipioId não fornecido.");
            return null;
        }

        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities`, municipioId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        }

        console.warn("Nenhum documento de município encontrado para o ID:", municipioId);
        return null;
    } catch (error) {
        console.error("Erro ao buscar dados do município:", error);
        throw error;
    }
};

/**
 * Busca os dados de um usuario DP especifico pelo seu ID.
 */
export const getRhUserById = async (rhUserId) => {
    try {
        if (!rhUserId) {
            console.warn("getRhUserById: rhUserId não fornecido.");
            return null;
        }

        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/rhUsers`, rhUserId);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        }

        console.warn("Nenhum usuário DP encontrado para o ID:", rhUserId);
        return null;
    } catch (error) {
        console.error("Erro ao buscar dados do usuário DP:", error);
        throw error;
    }
};

/**
 * Busca os dados de um servidor na colecao DadosServidores pelo seu CPF.
 */
export const getDadosServidorByCPF = async (municipioId, cpf) => {
    try {
        if (!municipioId || !cpf) {
            console.warn("getDadosServidorByCPF: municipioId ou cpf não fornecidos.");
            return null;
        }

        const cleanedCpf = cpf.replace(/[.-]/g, '');
        const docRef = doc(db, `artifacts/${firestorePathId}/public/data/municipalities/${municipioId}/DadosServidores`, cleanedCpf);
        const docSnap = await getFirestoreDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        }

        console.warn("Nenhum dado de servidor encontrado para o CPF:", cleanedCpf, "no município:", municipioId);
        return null;
    } catch (error) {
        console.error("Erro ao buscar dados do servidor por CPF:", error);
        throw error;
    }
};
